import { Box, Checkbox, Chip, Radio, Skeleton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useIntl } from 'react-intl';

import {
  deriveCategoryFromAnalyticalVariable,
  formatBoldText,
  type AnalyticalVariableListItem,
} from '@verantos-dev/feature-analytical-variables';
import { Tooltip } from '@verantos-dev/shared-components';
import { borderRadius, opacity } from '@verantos-dev/shared-ui-components/tokens';

import type { AvIneligibilityReason } from '../../utils/avLibraryEligibility';

const AvLibraryListCardSkeleton = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="text" width={160} height={24} />
          <Skeleton
            variant="rounded"
            width={64}
            height={22}
            sx={{ borderRadius: `${borderRadius.xl}px` }}
          />
        </Box>
      </Box>
      <Box sx={{ pl: 8 }}>
        <Skeleton variant="text" width="90%" height={18} />
        <Skeleton variant="text" width="60%" height={16} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  );
};

export const AvLibraryListSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <AvLibraryListCardSkeleton />
    <AvLibraryListCardSkeleton />
    <AvLibraryListCardSkeleton />
  </Box>
);

export interface AvLibraryListCardProps {
  item: AnalyticalVariableListItem;
  isSelected: boolean;
  isInCohort?: boolean;
  isNameConflict?: boolean;
  /** Set when the cohort's conditional index cannot use this variable; disables the row and explains why. */
  disabledReason?: AvIneligibilityReason;
  multiple: boolean;
  onSelect: (avId: string) => void;
}

export const AvLibraryListCard = ({
  item,
  isSelected,
  isInCohort,
  isNameConflict,
  disabledReason,
  multiple,
  onSelect,
}: AvLibraryListCardProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const isDisabled = Boolean(isInCohort || isNameConflict || disabledReason);

  // Category is null for SQL-only AVs (no structured logic tree) — suppress
  // the chip in that case rather than substituting a default label; users
  // shouldn't need to see the underlying AV "type". `useAvLibraryItems`
  // filters SQL-only items out, but guard here too so any direct caller is safe.
  const category = item.jsonValue ? deriveCategoryFromAnalyticalVariable(item.jsonValue) : null;
  const categoryText = category ? category.charAt(0).toUpperCase() + category.slice(1) : null;

  const formatDisplay = item.format
    ? item.format.charAt(0).toUpperCase() + item.format.slice(1).toLowerCase()
    : null;

  const formatOwnerText =
    item.format || item.owner
      ? intl.formatMessage(
          { id: 'projects.analyticalVariables.library.addFromLibrary.formatOwner' },
          {
            format:
              formatDisplay ??
              intl.formatMessage({
                id: 'projects.analyticalVariables.library.unknown',
              }),
            owner:
              item.owner ??
              intl.formatMessage({
                id: 'projects.analyticalVariables.library.unknown',
              }),
          }
        )
      : null;

  const selectedBg = alpha(theme.palette.primary.main, opacity[8]);
  // TODO: no VDS token for this value — needs designer input (#000000 not in palette; opacity-4 not in scale)
  const hoverBg = alpha('#000000', 0.04);

  return (
    <Box
      data-testid={`add-variable-from-library-card-${item.analyticalVariableId}`}
      onClick={() => !isDisabled && onSelect(item.analyticalVariableId)}
      sx={{
        p: 2,
        borderRadius: 1,
        cursor: isDisabled ? 'default' : 'pointer',
        backgroundColor: isSelected ? selectedBg : 'transparent',
        opacity: isDisabled ? 0.7 : 1,
        transition: 'background-color 150ms',
        '&:hover': isDisabled ? {} : { backgroundColor: isSelected ? selectedBg : hoverBg },
      }}
    >
      <Tooltip
        followCursor
        id={`add-variable-from-library-card-${item.analyticalVariableId}-tooltip`}
        placement="bottom"
        title={
          isNameConflict
            ? intl.formatMessage(
                {
                  id: 'projects.analyticalVariables.library.addFromLibrary.nameConflictChip',
                },
                { name: item.dataElement || item.name }
              )
            : disabledReason
            ? intl.formatMessage(
                { id: `projects.analyticalVariables.library.ineligible.${disabledReason}` },
                { format: item.format }
              )
            : null
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* ContentGroup: title row + body description */}
          <Box>
            {/* Title row: control + name + chip (control's default padding provides the indent) */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {multiple ? (
                <Checkbox
                  checked={isSelected || isInCohort}
                  disabled={isDisabled}
                  color="primary"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => !isDisabled && onSelect(item.analyticalVariableId)}
                />
              ) : (
                <Radio
                  checked={isSelected}
                  disabled={isDisabled}
                  color="primary"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => !isDisabled && onSelect(item.analyticalVariableId)}
                />
              )}
              <Box sx={{ display: 'flex', columnGap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ color: isDisabled ? 'text.disabled' : 'text.primary' }}
                >
                  {item.dataElement || item.name}
                </Typography>
                {categoryText && (
                  <Chip
                    label={categoryText}
                    size="medium"
                    sx={{
                      // TODO: no VDS token for this value — #000000 not in palette; use theme.palette.action.selected only if semantic meaning aligns
                      backgroundColor: alpha('#000000', 0.08),
                    }}
                    disabled={isDisabled}
                  />
                )}
              </Box>
            </Box>
            {/* Body: description indented to align below name (pl 8 = spacing-key-8 = 40px) */}
            {item.description && (
              <Box sx={{ pl: 10 }}>
                <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.primary'}>
                  {formatBoldText(item.description)}
                </Typography>
              </Box>
            )}
          </Box>
          {/* Meta: format + owner, same indent as body */}
          {formatOwnerText && (
            <Box sx={{ pl: 10 }}>
              <Typography variant="caption" color={isDisabled ? 'text.disabled' : 'text.secondary'}>
                {formatOwnerText}
              </Typography>
            </Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};
